import { FormProvider, useForm } from 'react-hook-form';
import styles from './styles.module.scss';
import { useModalNavigation } from '@/hooks/modals/useModalNavigation';
import { ModalsKey } from '@/enums/modalsKey';
import { Button } from '@starsoft/common/components';
import useTranslation from 'next-translate/useTranslation';
import { useRecoveryPasswordReset } from '@/api/recovery-passwords';
import { FormProps } from './props';
import { useState } from 'react';
import { Nullable } from '@starsoft/common/interfaces';
import { useSelector } from 'react-redux';
import { RootState } from '@/lib/store';
import { useNotificationService } from '@/hooks/notifications/useNotificationService';
import ResetPasswordRenderer from './Renderer';

export default function ResetPassword() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const methods = useForm<FormProps>();
  const { handleSubmit, watch } = methods;
  const { NotificationsService } = useNotificationService();
  const { navigateToModal } = useModalNavigation();
  const { reset, isPending } = useRecoveryPasswordReset();

  const selectedEmail: Nullable<string> = useSelector(
    (state: RootState) => state.auth.recoverEmail,
  );

  const code: string | undefined = watch('code');
  const password: string | undefined = watch('password');
  const passwordConfirmation: string | undefined = watch(
    'passwordConfirmation',
  );
  const isPasswordEmpty: boolean = password?.length === 0;
  const isValidPasswordLength: boolean =
    password?.length >= 4 && password?.length <= 128;

  const isValidPassword: boolean = isValidPasswordLength && !isPasswordEmpty;
  const isDisabled: boolean = !showPassword
    ? !code
    : !password || !passwordConfirmation || !isValidPassword;

  function handleBack() {
    if (!showPassword) {
      navigateToModal(ModalsKey.RecoverPassword);
      return;
    }

    setShowPassword(false);
  }

  function onSubmit(data: FormProps) {
    if (!showPassword) {
      setShowPassword(true);
      return;
    }

    if (!data?.code || !data?.password || !data?.passwordConfirmation) {
      return;
    }

    if (data.password != data.passwordConfirmation) {
      NotificationsService.error({
        title: 'confirm_password_error_title',
        message: 'confirm_password_error_description',
      });
      return;
    }

    reset({
      password: data.password,
      code: data.code,
      email: selectedEmail as string,
    });
  }

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.container__content}>
          <ResetPasswordRenderer
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            isPending={isPending}
          />
        </div>
        <div className={styles.container__buttons}>
          <Button type="button" isSecondary onClick={handleBack}>
            {t('modal:return_button')}
          </Button>
          <Button type="submit" isloading={isPending} disabled={isDisabled}>
            {t('common:advance')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
